import React, { useEffect, useState, useCallback } from "react";
import { Award } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PATH_ORDER, PATH_LABELS, pathSummary } from "@/lib/letterPolicy";

const PATH_HELP = {
  points: "Approved lettering points within a set number of school years.",
  performance: "Approved lettering submissions (any achievement) within a set number of school years.",
  active_years: "School years in a row on the team while in good standing. A year marked missed on the member breaks the streak.",
  subgroup: "Active member of a group this season. With a completion date, they must join before it and earn the award once it passes.",
  coach: "Lists coach awards as a path for members. You can always award by hand with a reason note.",
};

const num = (v, max, min = 1) => Math.min(max, Math.max(min, parseInt(v || 0, 10) || 0));

function NumIn({ value, onChange, testId, dis, w = "w-20", max = 8 }) {
  return <Input type="number" min="1" max={max} className={`h-8 ${w} tabular-nums`} value={value} disabled={dis} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
}

function PathFields({ kind, path, cfg, set, subgroups, dis }) {
  const t = `policy-${kind}`;
  if (path === "points" || path === "performance") {
    const key = path === "points" ? "points" : "count";
    return (<>
      <NumIn value={cfg[key]} max={path === "points" ? 100000 : 1000} onChange={(v) => set(key, v)} testId={`${t}-${path}-value`} dis={dis} />
      <span>{path === "points" ? "points" : "approved submissions"} within</span>
      <NumIn value={cfg.years} w="w-16" onChange={(v) => set("years", v)} testId={`${t}-${path}-years`} dis={dis} />
      <span>school year(s)</span>
    </>);
  }
  if (path === "active_years") {
    return (<><NumIn value={cfg.years} w="w-16" onChange={(v) => set("years", v)} testId={`${t}-active_years-value`} dis={dis} /><span>school year(s) in a row</span></>);
  }
  if (path !== "subgroup") return null;
  return (<>
    <Select value={cfg.subgroup_id || ""} onValueChange={(v) => set("subgroup_id", v)} disabled={dis}>
      <SelectTrigger className="h-8 w-44" data-testid={`${t}-subgroup-select`}><SelectValue placeholder={subgroups.length ? "Pick a group" : "No groups yet"} /></SelectTrigger>
      <SelectContent>{subgroups.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`${t}-subgroup-option-${s.id}`}>{s.name}</SelectItem>)}</SelectContent>
    </Select>
    <span className="text-xs text-slate-500">Completion date (optional)</span>
    <Input type="date" className="h-8 w-40" value={cfg.completion_date || ""} disabled={dis} onChange={(e) => set("completion_date", e.target.value || null)} data-testid={`${t}-subgroup-date`} />
  </>);
}

function PathRow({ kind, path, cfg, onChange, subgroups, dis }) {
  const set = (k, v) => onChange({ ...cfg, [k]: v });
  return (
    <div className={`rounded-lg border bg-white p-3 transition-colors ${cfg.enabled ? "border-violet-300" : "border-slate-200"}`} data-testid={`policy-${kind}-${path}`}>
      <div className="flex items-start justify-between gap-3">
        <div><p className="text-sm font-semibold text-slate-800">{PATH_LABELS[path]}</p><p className="text-xs text-slate-500">{PATH_HELP[path]}</p></div>
        <Switch checked={!!cfg.enabled} disabled={dis} onCheckedChange={(v) => set("enabled", v)} data-testid={`policy-${kind}-${path}-toggle`} />
      </div>
      {cfg.enabled && path !== "coach" && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-600"><PathFields kind={kind} path={path} cfg={cfg} set={set} subgroups={subgroups} dis={dis} /></div>
      )}
    </div>
  );
}

function PathSet({ kind, paths, onChange, subgroups, dis }) {
  return (
    <div className="space-y-2" data-testid={`policy-${kind}-paths`}>
      {PATH_ORDER.map((p) => <PathRow key={p} kind={kind} path={p} cfg={paths[p]} subgroups={subgroups} dis={dis} onChange={(c) => onChange({ ...paths, [p]: c })} />)}
    </div>
  );
}

const activeCount = (paths) => PATH_ORDER.filter((p) => paths?.[p]?.enabled).length;

function SectionTitle({ title, sub, paths, children }) {
  const n = activeCount(paths);
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <div className="mr-auto"><p className="font-heading text-sm font-bold text-violet-950">{title}</p><p className="text-xs text-slate-500">{sub}</p></div>
      {children}
      <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">{n} path{n === 1 ? "" : "s"} on</span>
    </div>
  );
}

const cleanPaths = (ps) => ({
  points: { enabled: !!ps.points.enabled, points: num(ps.points.points, 100000), years: num(ps.points.years, 8) },
  performance: { enabled: !!ps.performance.enabled, count: num(ps.performance.count, 1000), years: num(ps.performance.years, 8) },
  active_years: { enabled: !!ps.active_years.enabled, years: num(ps.active_years.years, 8) },
  subgroup: { enabled: !!ps.subgroup.enabled, subgroup_id: ps.subgroup.subgroup_id || null, completion_date: ps.subgroup.completion_date || null },
  coach: { enabled: !!ps.coach.enabled },
});

// Lettering-award policy editor (coach or admin endpoints). First and Subsequent Letters each get their own paths.
export const LetterPolicyCard = ({ getUrl, putUrl, editable = true, header = null }) => {
  const [policy, setPolicy] = useState(null);
  const [subgroups, setSubgroups] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!getUrl) { setPolicy(null); return; }
    try { const { data } = await api.get(getUrl); setPolicy(data.policy); setSubgroups(data.subgroups || []); }
    catch { setPolicy(null); }
  }, [getUrl]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(putUrl, { first: cleanPaths(policy.first), subsequent: cleanPaths(policy.subsequent), subsequent_mirror: !!policy.subsequent_mirror });
      setPolicy(data.policy);
      toast.success("Lettering award policy saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!policy) return null;
  const dis = !editable;
  const set = (k, v) => setPolicy((p) => ({ ...p, [k]: v }));

  return (
    <Card className="mb-4 border-violet-200 bg-violet-50/40 p-5 shadow-sm" data-testid="letter-policy-card">
      <div className="mb-1 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-100 text-violet-600"><Award className="h-5 w-5" /></span>
        <div>
          <h3 className="font-heading text-sm font-bold text-slate-800">Lettering Award Policy</h3>
          <p className="text-xs text-slate-500">Turn on one or more paths for each award. A member earns it by meeting <span className="font-semibold">any one</span> path. One award per school year.</p>
        </div>
      </div>
      {header}

      <div className="mt-4 grid gap-5 lg:grid-cols-2">
        <section data-testid="policy-first-section">
          <SectionTitle title="First Letter" sub="The member's first lettering award." paths={policy.first} />
          <PathSet kind="first" paths={policy.first} subgroups={subgroups} dis={dis} onChange={(v) => set("first", v)} />
        </section>
        <section data-testid="policy-subsequent-section">
          <SectionTitle title="Subsequent Letter" sub="Available from the school year after the First Letter." paths={policy.subsequent_mirror ? policy.first : policy.subsequent}>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
              <Switch checked={!!policy.subsequent_mirror} disabled={dis} onCheckedChange={(v) => set("subsequent_mirror", v)} data-testid="policy-subsequent-mirror" />
              Same as First Letter
            </label>
          </SectionTitle>
          {policy.subsequent_mirror
            ? <div className="rounded-lg border border-dashed border-violet-300 bg-white p-4 text-sm text-slate-600" data-testid="policy-subsequent-mirror-note">Uses the First Letter paths: <span className="font-semibold text-violet-900">{pathSummary(policy.first)}</span>. Each award counts fresh from the year after the previous one.</div>
            : <PathSet kind="subsequent" paths={policy.subsequent} subgroups={subgroups} dis={dis} onChange={(v) => set("subsequent", v)} />}
        </section>
      </div>

      {editable && (
        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={saving} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="policy-save-btn">{saving ? "Saving…" : "Save policy"}</Button>
        </div>
      )}
    </Card>
  );
};

export default LetterPolicyCard;
